import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api_middleware";
import type { FormAnalysis } from "../types/formTypes";

// Fetch all assessments
export const fetchAllAssessments = createAsyncThunk(
  "assessments/fetchAllAssessments",
  async (params?: {
    organizationId?: string;
    environment?: string;
    scenarioCategory?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.organizationId)
      queryParams.append("organization_id", params.organizationId);
    if (params?.environment)
      queryParams.append("environment", params.environment);
    if (params?.scenarioCategory)
      queryParams.append("scenario_category", params.scenarioCategory);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());

    const response = await api(
      `v1/admin/assessments?${queryParams.toString()}`,
      "GET"
    );
    return response;
  }
);

// Fetch a single assessment by ID
export const fetchAssessment = createAsyncThunk(
  "assessments/fetchAssessment",
  async (params: { assessmentId: string; organizationId: string }) => {
    const { assessmentId, organizationId } = params;
    const response = await api(
      `v1/admin/assessments/${assessmentId}?organization_id=${organizationId}`,
      "GET"
    );
    return response;
  }
);

// Create a new assessment
export interface CreateAssessmentParams {
  organization_id: string;
  scenario_description: string;
  environment?: string;
  context?: string;
  display_title?: string;
  category?: string;
}

export const createAssessment = createAsyncThunk(
  "assessments/createAssessment",
  async (params: CreateAssessmentParams) => {
    const response = await api(`v1/admin/assessments/`, "POST", params);
    return response;
  }
);

// Update an assessment
export interface UpdateAssessmentParams {
  id: string;
  interview_plan: any; // Full interview plan object
  organization_id?: string; // Optional organization_id
}

// Update assessment form schema specifically
export interface UpdateAssessmentFormSchemaParams {
  assessment_id: string;
  form_output_schema: any; // Form output schema
  organization_id?: string; // Optional organization_id
}

export const updateAssessment = createAsyncThunk(
  "assessments/updateAssessment",
  async (params: UpdateAssessmentParams) => {
    const { id, interview_plan, organization_id } = params;
    const body: any = { interview_plan };
    if (organization_id) {
      body.organization_id = organization_id;
    }
    const response = await api(`v1/admin/assessments/${id}`, "PUT", body);
    return response;
  }
);

export const updateAssessmentFormSchema = createAsyncThunk(
  "assessments/updateAssessmentFormSchema",
  async (params: UpdateAssessmentFormSchemaParams) => {
    const { assessment_id, form_output_schema, organization_id } = params;

    const body: any = {
      assessment_id,
      form_output_schema,
    };
    if (organization_id) {
      body.organization_id = organization_id;
    }
    const response = await api(
      `v1/admin/assessments/${assessment_id}/form-schema`,
      "PUT",
      body
    );
    return response;
  }
);

// Delete an assessment
export const deleteAssessment = createAsyncThunk(
  "assessments/deleteAssessment",
  async (assessmentId: string) => {
    const response = await api(
      `v1/admin/assessments/${assessmentId}`,
      "DELETE"
    );
    return { id: assessmentId, ...response };
  }
);

export interface AssessmentConsumptionPolicy {
  max_num_converted_sessions_per_user?: number | null;
  email_domains_allowlist?: string[] | null;
  email_allowlist?: string[] | null;
}

export interface FeatureInsightsConfig {
  task_type: "default" | "battlecard" | "hiring" | "opinions";
  task_role_name: string;
  task_role: string;
  task_goal: string;
  task_style: string;
  min_words_between_insights: number;
  max_insights_per_session: number;
  battlecard_namespace_assets?: {
    namespace_assets: Record<string, string[]>;
  } | null;
  namespace?: string;
  collection_name?: string | string[];
}

// Create assessment link
export interface CreateAssessmentLinkParams {
  assessmentId: string;
  organizationId: string;
  agent_config_id: string;
  display_name?: string | null;
  test_override_human_description?: string;
  additional_context?: string;
  control_graph_key?: string | null;
  namespace_asset_registry?: {
    namespace_assets: Record<string, string[]>;
  } | null;
  consumption_policy?: AssessmentConsumptionPolicy | null;
  insights_config?: FeatureInsightsConfig | null;
}

export interface CreateAssessmentLinkResponse {
  link_id: string;
  resource_link: string;
}

export const createAssessmentLink = createAsyncThunk(
  "assessments/createAssessmentLink",
  async (params: CreateAssessmentLinkParams) => {
    const {
      assessmentId,
      organizationId,
      agent_config_id,
      display_name,
      test_override_human_description,
      additional_context,
      control_graph_key,
      namespace_asset_registry,
      consumption_policy,
      insights_config,
    } = params;
    const response = await api(
      `v1/admin/assessments/${assessmentId}/create-link?organization_id=${organizationId}`,
      "POST",
      {
        agent_config_id,
        display_name,
        test_override_human_description,
        additional_context,
        control_graph_key,
        namespace_asset_registry,
        consumption_policy,
        insights_config,
      }
    );
    return response as CreateAssessmentLinkResponse;
  }
);

export const updateAssessmentLink = createAsyncThunk(
  "assessments/updateAssessmentLink",
  async (params: {
    linkId: string;
    organization_id: string;
    agent_config_id?: string;
    display_name?: string | null;
    test_override_human_description?: string | null;
    additional_context?: string | null;
    control_graph_key?: string | null;
    namespace_asset_registry?: {
      namespace_assets: Record<string, string[]>;
    } | null;
    consumption_policy?: AssessmentConsumptionPolicy | null;
    insights_config?: FeatureInsightsConfig | null;
  }) => {
    const { linkId, ...updateData } = params;
    const response = await api(
      `v1/admin/assessment-links/${linkId}`,
      "PUT",
      updateData
    );
    return response;
  }
);

// Fetch assessment links
export interface AssessmentLink {
  id: string;
  behavior_type: string; // "assessment", "roleplay_demo", etc.
  agent_config_id: string | null;
  created_at: string;
  creator_user_id: string;
  assessment_id: string | null;
  assessment_key?: string | null;
  roleplay_id?: string | null;
  roleplay_key?: string | null;
  display_name?: string | null;
  test_override_human_description?: string | null;
  additional_context?: string | null;
  control_graph_key?: string | null;
  namespace_asset_registry?: {
    namespace_assets: Record<string, string[]>;
  } | null;
  consumption_policy?: AssessmentConsumptionPolicy | null;
  insights_config?: FeatureInsightsConfig | null;
}

export const fetchAssessmentLinks = createAsyncThunk(
  "assessments/fetchAssessmentLinks",
  async (params: { assessmentId: string; organizationId: string }) => {
    const { assessmentId, organizationId } = params;
    const response = await api(
      `v1/admin/assessments/${assessmentId}/links?organization_id=${organizationId}`,
      "GET"
    );
    return response as AssessmentLink[];
  }
);

export const fetchOrganizationAssessmentLinks = createAsyncThunk(
  "assessments/fetchOrganizationAssessmentLinks",
  async (organizationId: string) => {
    const response = await api(
      `v1/admin/organizations/${organizationId}/assessment-links`,
      "GET"
    );
    return response as AssessmentLink[];
  }
);

// Fetch assessed sessions for an assessment
export interface AssessedSession {
  id: string;
  _ts?: number; // CosmosDB timestamp
  organization_id: string;
  user_id: string;
  conversation_id: string;
  chat_session_id: string;
  source_assessment_id?: string | null;
  source_roleplay_id?: string | null;
  state: "created" | "active" | "processing" | "ready";
  created_at?: string;
  processing_type?: "memory" | "form" | "roleplay";
  analysis_graph_info?: {
    type: string;
    version: string;
    namespace: string;
    major_version: number;
    minor_version: number;
    ready: boolean;
    transaction_index: number;
  } | null;
  analysis_results?: any | null;
  form_analysis?: FormAnalysis | null;
  roleplay_analysis?: {
    concern_scores: Record<
      string,
      {
        score: number;
        justification: string;
      }
    >;
  } | null;
  assessment_score?: number | null;
  duration_in_min?: number | null;
  archived?: boolean;
  spam?: boolean;
}

export const fetchAssessedSessions = createAsyncThunk(
  "assessments/fetchAssessedSessions",
  async (params: {
    assessmentId: string;
    organizationId: string;
    version?: number;
    minMajorVersion?: number;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append("organization_id", params.organizationId);
    if (params.version !== undefined)
      queryParams.append("version", params.version.toString());
    if (params.minMajorVersion !== undefined)
      queryParams.append(
        "min_major_version",
        params.minMajorVersion.toString()
      );

    const response = await api(
      `v1/admin/assessments/${
        params.assessmentId
      }/assessed-sessions?${queryParams.toString()}`,
      "GET"
    );
    return response as AssessedSession[];
  }
);

// Fetch a single assessed session
export const fetchAssessedSession = createAsyncThunk(
  "assessments/fetchAssessedSession",
  async (params: { sessionId: string; organizationId: string }) => {
    const response = await api(
      `v1/admin/assessed-sessions/${params.sessionId}?organization_id=${params.organizationId}`,
      "GET"
    );
    return response as AssessedSession;
  }
);

// Fetch assessment history by resource key
export const fetchAssessmentHistory = createAsyncThunk(
  "assessments/fetchAssessmentHistory",
  async (params: { resourceKey: string; organizationId: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append("resource_key", params.resourceKey);
    queryParams.append("organization_id", params.organizationId);

    const response = await api(
      `v1/admin/assessments/history/by-key?${queryParams.toString()}`,
      "GET"
    );
    return response;
  }
);
